import { inject, Injectable } from '@angular/core';
import { type MonoTypeOperatorFunction, type Observable, of, tap } from 'rxjs';

import { RuntimeErrorMessage } from '@blaze-runtime/engine-domain';
import {
  CascadeDeleteOption,
  ComparisonOperator,
  type LogicalPredicateNode,
  LogicalType,
  PredicateType,
} from '@blaze-shared/api-contracts-domain';
import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import { type DatasetRowDeleteOptions } from '@blaze-shared/schema-data-access';
import { isNullishOrEmptyString } from '@blaze-shared/shared-util';
import { type DeleteWorkflowNode } from '@blaze-shared/workflow-domain';

import { getRowId } from '../../dataset-row-id';
import { type NodeInput } from '../../node-input-rows';
import { NodeResultMessagesService } from '../../node-result-messages.service';

/**
 * Deletes dataset rows.
 *
 * Which rows go depends on how the node is configured and what came in: a current-row node deletes
 * the rows its input identifies by id, any other node deletes whatever its query matches — once
 * per input row, since the query is evaluated in each row's context.
 */
@Injectable({ providedIn: 'root' })
export class DatasetDeleteActionService {
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly iss = inject(IntelliSenseService);
  private readonly nodeResultMessages = inject(NodeResultMessagesService);
  private readonly notificationService = inject(NotificationService);
  private readonly schemaService = inject(SchemaService);

  datasetDelete(
    node: DeleteWorkflowNode,
    nodeInput: any,
    ctx: RuntimeContext,
    workflowId?: string,
    workflowExecutionId?: string,
  ): Observable<any> {
    const { errorMessage } = this.nodeResultMessages.getErrorAndSuccessMessage(
      node,
      nodeInput,
      ctx,
    );

    if (Array.isArray(nodeInput) && nodeInput?.length) {
      return this.deleteRows(node, nodeInput, ctx, errorMessage);
    }
    if (Array.isArray(nodeInput) && !nodeInput.length) {
      return of([]);
    }
    return this.deleteSingleRow(
      node,
      nodeInput,
      ctx,
      errorMessage,
      workflowId,
      workflowExecutionId,
    );
  }

  private deleteRows(
    node: DeleteWorkflowNode,
    nodeInput: any[],
    ctx: RuntimeContext,
    errorMessage: string,
  ): Observable<any> {
    const idCol = node.currentRow ? this.schemaService.getIdColumnName(node.datasetId) : undefined;
    const queries: LogicalPredicateNode[] = [];
    const idsToDelete: string[] = [];

    for (const row of nodeInput) {
      const context = this.iss.updateRuntimeContext(ctx, row);
      if (idCol) {
        idsToDelete.push(row[idCol]);
      } else {
        queries.push(this.createQuery(node, context));
      }
    }
    if (idCol) {
      queries.push({
        nodeType: PredicateType.Logical,
        logicalType: LogicalType.And,
        children: [
          {
            names: [idCol],
            nodeType: PredicateType.Comparison,
            op: ComparisonOperator.In,
            value: idsToDelete,
          },
        ],
      });
    }

    return this.datasetRowsService
      .deleteBatch({
        cascadeDeleteOptions: [node.cascadeDeleteOption || CascadeDeleteOption.None],
        datasetId: node.datasetId,
        errorMessage,
        ignoreResult: true,
        queries,
        showThemedNotification: true,
      })
      .pipe(this.notifyDeleted(node, ctx));
  }

  private deleteSingleRow(
    node: DeleteWorkflowNode,
    nodeInput: any,
    ctx: RuntimeContext,
    errorMessage: string,
    workflowId?: string,
    workflowExecutionId?: string,
  ): Observable<any> {
    const deleteOptions: DatasetRowDeleteOptions = {
      cascadeDeleteOption: node.cascadeDeleteOption,
      errorMsg: errorMessage,
      showThemedNotification: true,
      workflowExecutionId,
      workflowId,
      ...(node.currentRow
        ? { rowId: this.resolveCurrentRowId(node, ctx) }
        : {
            queryNode: this.createQuery(node, this.iss.updateRuntimeContext(ctx, nodeInput)),
          }),
    };
    return this.datasetRowsService
      .delete(node.datasetId, deleteOptions)
      .pipe(this.notifyDeleted(node, ctx));
  }

  /**
   * @throws when the context carries no row id — deleting the "current row" without knowing which
   * row that is would delete by an empty id.
   */
  private resolveCurrentRowId(node: DeleteWorkflowNode, ctx: RuntimeContext): string {
    const idCol = this.schemaService.getIdColumnName(node.datasetId);
    const rowId = getRowId(idCol, ctx);
    if (isNullishOrEmptyString(rowId)) {
      console.error(`Row ID not found in row: ${rowId}, ID column: ${idCol}`);
      throw new Error(RuntimeErrorMessage.MissingRowInfo);
    }
    return rowId;
  }

  private createQuery(node: DeleteWorkflowNode, context: RuntimeContext): LogicalPredicateNode {
    return this.iss.createDatasetQuery(
      node.query,
      node.queryConditions,
      node.condition || '',
      node.queryLogicalOp,
      context,
    );
  }

  /** Reports the deletion with the node's success message, evaluated against the deleted rows. */
  private notifyDeleted<T extends NodeInput>(
    node: DeleteWorkflowNode,
    ctx: RuntimeContext,
  ): MonoTypeOperatorFunction<T> {
    return tap<T>((deletedRows) => {
      const { successMessage } = this.nodeResultMessages.getErrorAndSuccessMessage(
        node,
        deletedRows,
        ctx,
      );
      this.notificationService.success(successMessage, true);
    });
  }
}
